import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import FragHans from './FragHans';
import { Battery, Sun, Shield, Wifi, Wrench, Zap, ArrowRight, ExternalLink, MessageCircle } from 'lucide-react';

const trustItems = [
  { icon: <Wrench className="w-5 h-5" />, label: "Made in Germany" },
  { icon: <Shield className="w-5 h-5" />, label: "Datensouveränität" },
  { icon: <Zap className="w-5 h-5" />, label: "Netzersatz serienmäßig" },
  { icon: <Wifi className="w-5 h-5" />, label: "Echtzeit-Monitoring" },
];

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />

      <main className="flex-1">
        {/* Hero */}
        <section className="ees-hero-gradient py-14 sm:py-24 text-center" data-testid="landing-hero">
          <div className="ees-container">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#32373c] mb-4 leading-tight">
              Finden Sie Ihr passendes<br />
              <span className="text-[#9ec043]">HANS Energiespeichersystem</span>
            </h1>
            <p className="text-base sm:text-lg text-gray-500 max-w-xl mx-auto mb-10 leading-relaxed">
              In wenigen Schritten zur unverbindlichen Erstempfehlung.<br className="hidden sm:block" />
              Für Privatkunden, Vertriebler und Gewerbebetriebe.
            </p>

            <div className="ees-trust-row mb-10">
              {trustItems.map((t, i) => (
                <div key={i} className="flex items-center gap-2 text-gray-400 text-xs sm:text-sm">
                  <span className="text-[#9ec043]">{t.icon}</span>
                  {t.label}
                </div>
              ))}
            </div>

            {/* Frag HANS Teaser (~1/4 Höhe) */}
            <div className="max-w-3xl mx-auto mb-10" data-testid="hans-teaser">
              <button
                type="button"
                onClick={() => navigate('/frag-hans')}
                className="w-full flex items-center gap-2 mb-2 text-xs text-gray-400 hover:text-[#9ec043] transition-colors"
                data-testid="hans-teaser-open-full"
              >
                <MessageCircle className="w-3.5 h-3.5" />
                <span>Lieber mit Hans sprechen? Beschreibe dein Vorhaben in eigenen Worten.</span>
              </button>
              <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden" style={{ height: '300px' }}>
                <FragHans variant="teaser" />
              </div>
            </div>

            {/* Module Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 max-w-3xl mx-auto">
              <button
                onClick={() => navigate('/konfigurator')}
                className="ees-card-hover group text-left rounded-xl border border-gray-200 hover:border-[#9ec043]/40 bg-white p-6 sm:p-8 transition-all"
                data-testid="module-general-card"
              >
                <div className="w-12 h-12 rounded-lg bg-[#9ec043]/10 flex items-center justify-center mb-4">
                  <Battery className="w-6 h-6 text-[#9ec043]" />
                </div>
                <h2 className="text-lg sm:text-xl font-semibold text-[#32373c] mb-2">Allgemeiner Systemkonfigurator</h2>
                <p className="text-sm text-gray-500 mb-4 leading-relaxed">
                  Lassen Sie sich basierend auf Ihrem Verbrauch und Ihren Anforderungen das passende EES-System empfehlen.
                </p>
                <div className="flex items-center gap-2 text-[#9ec043] text-sm font-medium group-hover:gap-3 transition-all">
                  Konfigurator starten <ArrowRight className="w-4 h-4" />
                </div>
              </button>

              <button
                onClick={() => navigate('/pv-konfigurator')}
                className="ees-card-hover group text-left rounded-xl border border-gray-200 hover:border-[#9ec043]/40 bg-white p-6 sm:p-8 transition-all"
                data-testid="module-pv-card"
              >
                <div className="w-12 h-12 rounded-lg bg-[#9ec043]/10 flex items-center justify-center mb-4">
                  <Sun className="w-6 h-6 text-[#9ec043]" />
                </div>
                <h2 className="text-lg sm:text-xl font-semibold text-[#32373c] mb-2">PV- und String-Konfigurator</h2>
                <p className="text-sm text-gray-500 mb-4 leading-relaxed">
                  Technische Vorauswahl für Hans HS PV und Hans HS PV duo. Prüfen Sie die Kompatibilität Ihrer PV-Strings.
                </p>
                <div className="flex items-center gap-2 text-[#9ec043] text-sm font-medium group-hover:gap-3 transition-all">
                  PV-Konfigurator starten <ArrowRight className="w-4 h-4" />
                </div>
              </button>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="ees-section-grey py-12 sm:py-16" data-testid="landing-benefits">
          <div className="ees-container text-center">
            <h2 className="text-xl sm:text-2xl font-semibold text-[#32373c] mb-8">Was HANS-Speicher besonders macht</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
              {[
                { title: "Kundenspezifisch gebaut", desc: "Jedes System wird passend zu Ihren Anforderungen konfiguriert und vorkonfiguriert ausgeliefert." },
                { title: "Plug and Play", desc: "Vorkonfiguriert und fertig eingerichtet. Schnelle Installation ohne aufwändige Programmierung." },
                { title: "Modular erweiterbar", desc: "Batterie und Leistung jederzeit nachrüstbar. Ihr System wächst mit Ihren Anforderungen." },
                { title: "Direkter Herstellersupport", desc: "Entwickelt und produziert in Deutschland. Persönliche Ansprechpartner ohne Umwege." },
                { title: "Netzersatz serienmäßig", desc: "Vollwertiger dreiphasiger Netzersatz ab Werk. Keine zusätzliche Umschaltbox nötig." },
                { title: "Smart Grid Ready", desc: "Dynamische Strompreisanbindung, Wärmepumpen- und Ladesäulenansteuerung inklusive." },
              ].map((b, i) => (
                <div key={i} className="text-left rounded-lg border border-gray-200 bg-white p-5">
                  <h3 className="text-sm font-semibold text-[#32373c] mb-1.5">{b.title}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-gray-200 py-6 bg-white" data-testid="landing-footer">
        <div className="ees-container flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-gray-400">EES Effiziente Energiespeichersysteme GmbH</p>
          <a href="https://www.ees-energiespeicher.de/" target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#9ec043] transition-colors" data-testid="footer-ees-link">
            Zur EES-Startseite <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </footer>
    </div>
  );
}
